import React, { Component } from 'react';
import uuid from 'uuid/v4';

import styled from 'styled-components';

import Text from './text';
import ManagedComponent from '../components/managed-component';
import Section, { SectionChild } from '../components/section';
import SmartView from '../components/smart-view';
import ContentBlock from '../components/content-block';

import AndroidSpinner from '../icons/android-loading.svg';
import AndroidClear from '../icons/android-clear.svg';
import IOSSpinner from '../icons/iOS-loading.svg';
import SearchIconSVG from '../icons/Search.svg';
import IOSClear from '../icons/iOS-X.svg';

import {
  FULL_VH,
  NavBackLabel
} from '../components/Common';

import theme, {
  isThemeiOS,
  fitbitGreenText
} from '../themes';

const SmartButton = styled.button`
  display: flex;
  border: 0;
  padding: 0;
  background-color: white;
  width: 100%;
  justify-content: space-between;
  font-size: 18px;

  & .item-content {
    width: 100%;
  }
`;

const AddItem = styled.span`
  text-align: center;
  width: 100%;
  color: #2ab7be;
`;

const Loading = styled.div.attrs({
  dangerouslySetInnerHTML: {
    __html: isThemeiOS() ? IOSSpinner : AndroidSpinner
  }
})`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: ${FULL_VH - theme.AutocompleteMargin}px;
  width: 100vw;
  margin-top: 20px;
  > svg {
    ${theme.LoadingIcon}
  }
`;

const ClearIcon = styled.span.attrs({
  dangerouslySetInnerHTML: {
    __html: isThemeiOS() ? IOSClear : AndroidClear
  }
})`
  text-align: center;
  ${theme.ClearIcon};
`;

const SearchIcon = styled.div.attrs({
  dangerouslySetInnerHTML: { __html: SearchIconSVG }
})`
  display: inline-block;
  ${({ focus }) => theme.SearchIcon({ focus })}
`;

const SearchBar = styled.div`
  background: white;
  ${({ autocomplete }) => theme.SearchBar({ autocomplete })}
`;

const SearchBarInner = styled.div`
  align-content: space-around;
  align-items: center;
  display: flex;
  justify-content: space-evenly;
  > * {
    border: none;
  }
  > input {
    caret-color: ${fitbitGreenText};
    flex-grow: 1;
    &:focus {
      outline-color: transparent;
    }
  }
  ${theme.SearchBarInner};
`;

const ScrollableSection = styled.div`
  height: ${FULL_VH - theme.AutocompleteMargin}px;
  overflow-y: auto;
  position: relative;
`;

const AutocompleteItems = styled(Section)`
  ${theme.AutocompleteItems}
`;

const Value = styled.div`
  ${({ isPassword }) => isPassword && `
    -webkit-text-security: disc;
    -moz-text-security: disc;
    text-security: disc;
  `}
`;

class TextInput extends Component {
  static _type = 'TextInput';
  _type = 'TextInput';

  static defaultRender = ({name}) => <Text key={name}>{name}</Text>;

  static defaultProps = {
    action: 'Save',
    type: 'text',
    renderItem: TextInput.defaultRender
  };

  state = {
    currentText: this.value || '',
    id: `select-${uuid()}`,
    smartView: false,
    autocompleteList: [],
    autocompleteLoading: false,
    inputFocus: false
  };

  get value() {
    const { value, useSimpleValue } = this.props;

    if (typeof value === 'string') {
      return value;
    }

    if (!useSimpleValue && typeof value !== 'undefined') {
      return value && value.name;
    }

    return '';
  }

  // Simple debounce logic
  handleAutocomplete = (() => {
    let timeout;
    return (value) => {
      const { debounce = 250, onAutocomplete } = this.props;

      if (onAutocomplete) {
        const callLater = () => {
          timeout = null;
          Promise.resolve(onAutocomplete(value)).then((options) => {
            this.setState(({ currentText }) => {
              if (currentText !== value) {
                return;
              }

              return {
                autocompleteLoading: false,
                autocompleteList: options
              };
            });
          });
        };

        clearTimeout(timeout);
        timeout = setTimeout(callLater, debounce);
      }
    }
  })();

  handleChange = (value) => {
    const { onAutocomplete } = this.props;
    let { autocompleteLoading } = this.state;

    if (onAutocomplete) {
      this.handleAutocomplete(value);
      autocompleteLoading = true;
    }

    this.setState({
      currentText: value,
      autocompleteLoading
    })
  }

  onSave = (option = { name: this.state.currentText }) => {
    const { useSimpleValue, onChange } = this.props;

    this.setState(() => {
      if (typeof onChange === 'function') {
        onChange(useSimpleValue ? option.name : option);
      }

      return {
        currentText: option.name,
        autocompleteList: [],
        smartView: false
      };
    });
  };

  clearValue = () => {
    this.setState({
      currentText: '',
      autocompleteList: []
    });
  }

  renderAutocompleteList = () => {
    const { renderItem, renderAutocomplete = renderItem } = this.props;
    const { currentText, autocompleteList } = this.state;

    if (!autocompleteList || autocompleteList.length === 0) {
      return;
    }
    return (
      <ScrollableSection>
        <AutocompleteItems>
          {autocompleteList.map((option, index) => (
            <ContentBlock key={option.name}>
              <SmartButton key={option.name} onClick={() => this.onSave(option)}>
                {renderAutocomplete(option, currentText)}
              </SmartButton>
            </ContentBlock>
          ))}
        </AutocompleteItems>
      </ScrollableSection>
    );
  };

  renderSmartView = () => {
    const {
      label,
      type,
      placeholder = (typeof label === 'string' ? label : ''),
      action,
      onAutocomplete
    } = this.props;

    const {
      autocompleteLoading,
      currentText,
      inputFocus
    } = this.state;

    let actionView = (
      <Section>
        <ContentBlock className="content-block">
          <SmartButton className="smart-button" onClick={() => this.onSave()}>
            <AddItem>{action}</AddItem>
          </SmartButton>
        </ContentBlock>
      </Section>
    );

    if (onAutocomplete) {
      actionView = autocompleteLoading ?
        <Loading /> :
        this.renderAutocompleteList();
    }

    const clearIcon = currentText ? <ClearIcon onClick={this.clearValue} /> : undefined;
    return (
      <div>
        <Section
          title={!onAutocomplete && label}
          noMargin={onAutocomplete || !isThemeiOS()}
          noPadding={onAutocomplete}
          noTopBorder={onAutocomplete}
        >
          <SearchBar autocomplete={!!onAutocomplete}>
            <SearchBarInner>
              <div className="search">
                {onAutocomplete &&
                  <SearchIcon focus={inputFocus} />
                }
              </div>
              <input
                type={type}
                value={currentText}
                onBlur={() => this.setState({ inputFocus: false })}
                onChange={({currentTarget}) => this.handleChange(currentTarget.value)}
                onFocus={() => this.setState({ inputFocus: true })}
                placeholder={placeholder}
              />
              {clearIcon}
            </SearchBarInner>
          </SearchBar>
        </Section>
        {actionView}
      </div>
    );
  };

  render() {
    const {
      title,
      label = title,
      type,
      disabled,
      actAsButton,
      resetOnClose,
      onAutocomplete
    } = this.props;

    const { id } = this.state;

    return (
      <SectionChild title={title}>
        <SmartView
          id={id}
          value={<Value isPassword={type === 'password'}>{this.value}</Value>}
          label={label}
          disabled={disabled}
          small={!onAutocomplete}
          navBackLabel={<NavBackLabel>Cancel</NavBackLabel>}
          navTitle={title}
          actAsButton={actAsButton}
          showSmartView={this.state.smartView}
          onToggleView={() => {
            this.setState({
              // Make sure currentText always represents the set value
              // unless resetOnClose is set
              currentText: resetOnClose ? '' : this.value,
              smartView: !this.state.smartView,
              autocompleteList: [],
              autocompleteLoading: false
            })
          }}
          renderSmartView={this.renderSmartView}
        />
      </SectionChild>
    );
  }
}

export default class extends Component {
  parseValue(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      return rawValue;
    }
  }

  render() {
    const {
      title,
      label,
      placeholder,
      type,
      disabled,
      actAsButton,
      resetOnClose,
      value,
      useSimpleValue,
      renderItem,
      onAutocomplete,
      onChange,
      settingsKey
    } = this.props;

    const textInputProps = {
      title,
      label,
      placeholder,
      type,
      disabled,
      actAsButton,
      resetOnClose,
      value,
      useSimpleValue,
      renderItem,
      onAutocomplete,
      onChange
    };

    if (!settingsKey || actAsButton) {
      return (
        <TextInput {...textInputProps} />
      );
    }

    return (
      <ManagedComponent settingsKey={settingsKey}>
        {({ value, onChange }) => {
          value = this.parseValue(value);

          return (
            <TextInput
              {...textInputProps}
              useSimpleValue={useSimpleValue}
              value={value}
              onChange={(value) => {
                if (typeof textInputProps.onChange === 'function') {
                  textInputProps.onChange(value);
                }
                onChange(useSimpleValue ? value : JSON.stringify(value));
              }}
            />
          );
        }}
      </ManagedComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/fitbit-components/text-input.js